import { RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';
import { getPrazoEnvioInfo } from './prazo-envio-utils';

export interface RelatorioHistoricoEvento {
  data: string;
  label: string;
  detalhe?: string;
  badge?: {
    text: string;
    title?: string;
  };
}

/**
 * Monta a linha do tempo do relatório (defensor ↔ diretor ↔ Corregedoria) para exibição em modal.
 * Baseado nos campos atuais do DTO; ciclos antigos podem não aparecer se o backend sobrescreveu datas.
 */
export function buildRelatorioHistoricoEventos(relatorio: RelatorioAtividadeDto): RelatorioHistoricoEvento[] {
  const eventos: RelatorioHistoricoEvento[] = [];
  const fmt = (d: string | undefined) => (d ? new Date(d).toISOString() : '');

  if (relatorio.dataCriacao) {
    eventos.push({ data: fmt(relatorio.dataCriacao), label: 'Criação do relatório' });
  }

  if (relatorio.dataEnvio) {
    const prazoInfo = getPrazoEnvioInfo(relatorio);
    const detalheBase = 'Encaminhado para análise e aprovação da diretoria regional';
    eventos.push({
      data: fmt(relatorio.dataEnvio),
      label: 'Envio do defensor para o diretor regional',
      detalhe: prazoInfo?.isForaPrazo
        ? `${detalheBase} (Fora do prazo - prazo final: ${prazoInfo.prazoFinalLabel})`
        : detalheBase,
      badge: prazoInfo?.isForaPrazo
        ? {
            text: 'Fora do prazo',
            title: `Prazo final: ${prazoInfo.prazoFinalLabel}`,
          }
        : undefined,
    });
  }

  /** Reprovação pela Diretoria ao defensor (ainda não havia aprovação do diretor neste ciclo). */
  if (relatorio.dataReprovacao && !relatorio.dataAprovacao) {
    const detalhe = [
      relatorio.reprovadoPorNome ? `Por: ${relatorio.reprovadoPorNome}` : '',
      relatorio.justificativaReprovacao ? `Justificativa: ${relatorio.justificativaReprovacao}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
    eventos.push({
      data: fmt(relatorio.dataReprovacao),
      label: 'Reprovação pela Diretoria regional ao defensor',
      detalhe: detalhe || undefined,
    });
  }

  if (relatorio.dataAprovacao) {
    eventos.push({
      data: fmt(relatorio.dataAprovacao),
      label: 'Aprovação pelo diretor regional',
      detalhe: relatorio.aprovadoPorNome ? `Aprovado por ${relatorio.aprovadoPorNome}` : undefined,
    });
    eventos.push({
      data: fmt(relatorio.dataAprovacao),
      label: 'Encaminhamento do relatório à Corregedoria',
      detalhe: 'Aguardando aval final da Corregedoria',
    });
  }

  const devDir = relatorio.dados?.devolucaoCorregedoriaAoDiretor as
    | { justificativa?: string; em?: string }
    | undefined;
  if (devDir?.em) {
    eventos.push({
      data: fmt(devDir.em),
      label: 'Devolução pela Corregedoria ao diretor regional',
      detalhe: devDir.justificativa ? `Justificativa: ${devDir.justificativa}` : undefined,
    });
  }

  /**
   * Reprovação pela Corregedoria ao defensor: havia aprovação do diretor e status ainda reprovado
   * (ou dados ainda refletem a reprovação antes de reenvio direto).
   */
  if (
    relatorio.dataReprovacao &&
    relatorio.dataAprovacao &&
    relatorio.status === 'reprovado'
  ) {
    const detalhe = [
      relatorio.reprovadoPorNome ? `Por: ${relatorio.reprovadoPorNome}` : '',
      relatorio.justificativaReprovacao ? `Justificativa: ${relatorio.justificativaReprovacao}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
    eventos.push({
      data: fmt(relatorio.dataReprovacao),
      label: 'Reprovação pela Corregedoria ao defensor',
      detalhe: detalhe || undefined,
    });
  }

  const envCor = relatorio.dados?.dataEnvioDiretoCorregedoriaDefensor as { em?: string } | undefined;
  if (envCor?.em) {
    eventos.push({
      data: fmt(envCor.em),
      label: 'Reenvio direto à Corregedoria pelo defensor',
      detalhe:
        'Após correções solicitadas pela Corregedoria (sem novo trâmite obrigatório pelo diretor regional)',
    });
  }

  if (relatorio.corregedoriaAprovadoEm) {
    eventos.push({
      data: fmt(relatorio.corregedoriaAprovadoEm),
      label: 'Aval final da Corregedoria — aprovação',
      detalhe: relatorio.corregedoriaAprovadoPorNome
        ? `Registrado por ${relatorio.corregedoriaAprovadoPorNome}`
        : undefined,
    });
  }

  eventos.sort((a, b) => a.data.localeCompare(b.data));
  return eventos;
}
